import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import {
  fetchAccountsReceivable,
  AccountsReceivable,
} from '../../lib/invoices/accountsReceivable';

interface AccountsReceivableTabProps {
  businessId: string;
  /** Completed bookings (legacy) merged in so A/R is complete. */
  bookings?: any[];
  refreshKey?: number;
  onOpenBookingInvoice?: (bookingId: string) => void;
  /** Opens a non-booking invoice (order / rental / counter sale / standalone). */
  onOpenStandaloneInvoice?: (invoiceId: string) => void;
  onReceivePayment?: (customerId: string, customerName: string) => void;
  onSendStatement?: () => void;
}

const fmtMoney = (n: number) =>
  '$' + (Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AccountsReceivableTab({ businessId, bookings = [], refreshKey, onOpenBookingInvoice, onOpenStandaloneInvoice, onReceivePayment, onSendStatement }: AccountsReceivableTabProps) {
  const [ar, setAr] = useState<AccountsReceivable | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    fetchAccountsReceivable(businessId, bookings)
      .then((data) => { if (active) setAr(data); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load receivables'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, bookings, refreshKey]);

  return (
    <div className="pb-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}>
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 px-4 py-4">
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Outstanding (A/R)</p>
          <p className="text-[16px] font-bold text-gray-900 truncate">{fmtMoney(ar?.totalOutstanding || 0)}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Overdue</p>
          <p className="text-[16px] font-bold text-red-600 truncate">{fmtMoney(ar?.totalOverdue || 0)}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Customers owing</p>
          <p className="text-[16px] font-bold text-gray-900 truncate">{ar?.customerCount || 0}</p>
        </div>
      </div>

      {/* Aging buckets */}
      {ar && ar.totalOutstanding > 0 && (
        <div className="px-4 pb-2">
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden flex">
            {ar.buckets.map((b, i) => (
              <div key={b.key} className={`flex-1 p-2.5 text-center ${i > 0 ? 'border-l border-gray-100' : ''}`}>
                <p className="text-[10px] font-medium text-gray-500 truncate">{b.label}</p>
                <p className={`text-[13px] font-bold truncate ${b.key === 'current' ? 'text-gray-900' : 'text-red-600'}`}>{fmtMoney(b.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {onSendStatement && ar && ar.customers.length > 0 && (
        <div className="px-4 pb-1 flex justify-end">
          <button
            type="button"
            onClick={onSendStatement}
            className="text-[13px] font-semibold text-blue-600 hover:text-blue-700"
          >
            Send Statements →
          </button>
        </div>
      )}

      {/* Customers */}
      <div className="px-4 pt-2">
        {loading ? (
          <div className="py-16 text-center text-gray-500 text-sm">Loading receivables…</div>
        ) : error ? (
          <div className="py-10 text-center text-red-600 text-sm">{error}</div>
        ) : !ar || ar.customers.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No outstanding balances. 🎉</p>
          </div>
        ) : (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100">
            {ar.customers.map((c) => {
              const key = c.customerId || c.customerName;
              const isOpen = expanded === key;
              return (
                <div key={key}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : key)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <span className="text-[15px] font-semibold text-gray-900 truncate">{c.customerName}</span>
                      <div className="text-[12px] text-gray-500 mt-0.5">
                        {c.invoiceCount} open {c.invoiceCount === 1 ? 'invoice' : 'invoices'}
                        {c.oldestDaysPastDue > 0 ? ` · oldest ${c.oldestDaysPastDue}d past due` : ''}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[15px] font-bold text-gray-900">{fmtMoney(c.outstanding)}</div>
                      {c.overdue > 0 && <div className="text-[11px] text-red-600 font-medium">{fmtMoney(c.overdue)} overdue</div>}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-3 pl-11 space-y-1.5">
                      {c.customerId && onReceivePayment && (
                        <div className="pb-1">
                          <button
                            type="button"
                            onClick={() => onReceivePayment(c.customerId!, c.customerName)}
                            className="text-[13px] font-semibold text-blue-600 hover:text-blue-700"
                          >
                            + Receive Payment
                          </button>
                        </div>
                      )}
                      {c.invoices.map((inv) => (
                        <div key={`${inv.kind}-${inv.id}`} className="flex items-center justify-between text-[13px]">
                          <button
                            type="button"
                            onClick={() => {
                              if (inv.bookingId) onOpenBookingInvoice?.(inv.bookingId);
                              else if (inv.kind === 'invoice') onOpenStandaloneInvoice?.(inv.id);
                            }}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {inv.kind === 'invoice' ? `Invoice #${inv.number}` : `Booking #${inv.number}`}
                          </button>
                          <div className="flex items-center gap-3">
                            <span className={inv.isOverdue ? 'text-red-600' : 'text-gray-500'}>
                              {inv.dueDate ? `Due ${formatDate(inv.dueDate)}` : formatDate(inv.invoiceDate)}
                              {inv.isOverdue ? ` · ${inv.daysPastDue}d` : ''}
                            </span>
                            <span className="font-semibold text-gray-900 w-20 text-right">{fmtMoney(inv.balance)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
